import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useUserDetails } from '../../../hooks/userDetails';
import RouteKey from '../../../routes/key';
import { Role } from '../../../models';
import { useAuth } from '../logic/auth';
import PrivateRoutePage from './PrivateRoutePage';

interface PrivateRouteProps {
  children: React.ReactNode;
  exact?: boolean;
  path: string;
  roles: Role[];
}

const { auth, error } = RouteKey.child;
const { login } = RouteKey.auth.child;
const { generic } = RouteKey.error.child;

function PrivateRoute({ children, exact, path, roles }: PrivateRouteProps) {
  const authHook = useAuth();
  const { data, isLoading, refetch } = useUserDetails();
  const history = useHistory();
  const hasAuthorizationPayload =
    Array.isArray(data?.data?.roles) && data.data.roles.length > 0;

  const expectedRole: string[] = Object.values(roles);
  const matchedRole = hasAuthorizationPayload && data?.data.roles
    ? data.data.roles.filter((f: any) => expectedRole.includes(f))
    : [];
  const isMatchedBefore = matchedRole.length > 0;

  useEffect(() => {
    if (hasAuthorizationPayload) {
      if (!isMatchedBefore) {
        history.replace(`/${error}/${generic}`);
      }
    }
  }, [hasAuthorizationPayload, history, isMatchedBefore]);

  useEffect(() => {
    if (!authHook.isUserLoggedIn || isLoading || hasAuthorizationPayload) {
      return;
    }

    // Logged-in session can briefly return user-details without roles (e.g. 202 propagation).
    // Poll until roles are available so the guard can resolve instead of spinning forever.
    const intervalId = setInterval(() => {
      refetch();
    }, 1000);

    return () => clearInterval(intervalId);
  }, [authHook.isUserLoggedIn, hasAuthorizationPayload, isLoading, refetch]);

  return (
    <PrivateRoutePage
      authHook={authHook}
      auth={auth}
      exact={exact}
      path={path}
      login={login}
      isMatchedBefore={isMatchedBefore}
      isLoading={isLoading}
    >
      {children}
    </PrivateRoutePage>
  );
}

export default PrivateRoute;
